import { supabase } from "@/integrations/supabase/client";
import { Store } from "@/models/storeModel";

export const addStore = async (
  storeData: Omit<Store, "id" | "created_at" | "updated_at">
): Promise<Store> => {
  // Get the current user session
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    throw new Error("Authentication required to create a store");
  }

  const user = session.user;

  try {
    // Verify user exists in the users table
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("id, role")
      .eq("id", user.id)
      .single();

    if (userError || !userData) {
      throw new Error(
        "User profile not found. Please complete registration first."
      );
    }

    // Map industry type to valid enum value
    const validIndustryType = storeData.industry_type as
      | "restaurant"
      | "cafe"
      | "bar"
      | "nightclub"
      | "hotel"
      | "spa"
      | "salon"
      | "tattoo"
      | "massage_therapy"
      | "catering"
      | "retail"
      | "fitness"
      | "beauty"
      | "healthcare"
      | "education"
      | "other"
      | null;

    // Generate slug if not provided
    let slug = storeData.slug;
    if (!slug && storeData.name) {
      const { data: generatedSlug, error: slugError } = await supabase.rpc(
        "generate_store_slug_from_name",
        { store_name: storeData.name }
      );

      if (!slugError && generatedSlug) {
        slug = generatedSlug;
      }
    }

    // Insert the store
    const { data: store, error: storeError } = await supabase
      .from("stores")
      .insert({
        name: storeData.name,
        address: storeData.address,
        phone: storeData.phone,
        email: storeData.email,
        is_active: storeData.is_active,
        is_b2b: storeData.isB2B || false,
        legal_name: storeData.legalName,
        tax_id: storeData.taxId,
        business_type: storeData.businessType,
        registration_number: storeData.registrationNumber,
        industry_type: validIndustryType || "other",
        payment_terms: storeData.paymentTerms,
        company_phone: storeData.companyPhone,
        company_address: storeData.companyAddress,
        logo: storeData.logo,
        advanced_inventory_tracking:
          storeData.advanced_inventory_tracking || false,
        owner_id: user.id,
        street: storeData.street,
        street_number: storeData.street_number,
        latitude: storeData.latitude,
        longitude: storeData.longitude,
        order_radius_meters: storeData.order_radius_meters || 1000,
        request_payment_mode: storeData.request_payment_mode,
        balance: storeData.balance,
        slug: slug,
      })
      .select()
      .single();

    if (storeError) {
      console.error("Error creating store:", storeError);
      throw new Error(storeError.message);
    }

    if (!store) {
      throw new Error("Failed to create store");
    }

    console.log("Store created successfully:", store);

    // Add the user as an admin employee
    const { error: employeeError } = await supabase
      .from("store_employees")
      .insert({
        store_id: store.id,
        user_id: user.id,
        first_name: user.user_metadata?.first_name || "",
        last_name: user.user_metadata?.last_name || "",
        email: user.email,
        role: "manager",
      });

    if (employeeError) {
      console.error("Error adding user as store employee:", employeeError);
      // Attempt to rollback store creation
      await supabase.from("stores").delete().eq("id", store.id);
      throw new Error("Failed to set up store permissions");
    }

    return {
      ...store,
      id: store.id,
      name: store.name,
      address: store.address,
      phone: store.phone,
      email: store.email,
      is_active: store.is_active,
      created_at: store.created_at,
      updated_at: store.updated_at,
      slug: store.slug,
      business_hours:
        typeof store.business_hours === "string"
          ? JSON.parse(store.business_hours)
          : store.business_hours,
    };
  } catch (error) {
    console.error("Error in addStore:", error);
    throw error instanceof Error ? error : new Error("Failed to create store");
  }
};
